/**
 * Eigentümer-Portal — Hausgeld-Übersicht (Phase 4).
 * Zeigt die eigenen Vorschüsse gruppiert nach Objekt und Jahr, mit Zahlungsstatus.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { CurrencyEur, CheckCircle, Warning, Clock } from "@phosphor-icons/react";

const MONTHS = ["Jan","Feb","Mär","Apr","Mai","Jun","Jul","Aug","Sep","Okt","Nov","Dez"];
const STATUS_META = {
  open:     { label: "Offen",       color: "bg-amber-100 text-amber-800",     icon: Clock },
  partial:  { label: "Teilzahlung", color: "bg-blue-100 text-blue-800",       icon: Warning },
  paid:     { label: "Bezahlt",     color: "bg-emerald-100 text-emerald-800", icon: CheckCircle },
  overpaid: { label: "Überzahlt",   color: "bg-purple-100 text-purple-800",   icon: Warning },
};
const eur = (v) => (Number(v) || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

export default function OwnerHausgeld() {
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/weg/portal/my-hausgeld")
      .then((r) => setCharges(r.data?.charges || []))
      .finally(() => setLoading(false));
  }, []);

  // Nach (Property, Jahr) gruppieren, dann nach Einheit
  const grouped = useMemo(() => {
    const out = {};
    charges.forEach((c) => {
      const propKey = c.property?.property_number || c.property_id;
      const key = `${propKey}::${c.year}`;
      if (!out[key]) out[key] = { property: c.property, year: c.year, byUnit: {}, sum: 0, paid: 0 };
      const uk = c.unit?.unit_number || c.unit_id;
      if (!out[key].byUnit[uk]) out[key].byUnit[uk] = { unit: c.unit, months: {}, year: 0, paid: 0 };
      out[key].byUnit[uk].months[c.month] = c;
      out[key].byUnit[uk].year += Number(c.amount || 0);
      out[key].byUnit[uk].paid += Number(c.paid_amount || 0);
      out[key].sum += Number(c.amount || 0);
      out[key].paid += Number(c.paid_amount || 0);
    });
    return out;
  }, [charges]);

  return (
    <div className="space-y-6" data-testid="owner-hausgeld-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <CurrencyEur weight="duotone" className="text-emerald-700" /> Hausgeld
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Ihre Vorschüsse aus dem Wirtschaftsplan der Eigentümergemeinschaft.
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Lade …</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-10 text-center bg-slate-50">
          <CurrencyEur size={40} className="mx-auto text-slate-400 mb-3" />
          <div className="text-sm text-slate-600">Aktuell keine Vorschüsse.</div>
        </div>
      ) : (
        Object.entries(grouped).map(([key, g]) => (
          <section key={key} className="space-y-3" data-testid={`owner-hausgeld-group-${key}`}>
            <div className="flex items-baseline justify-between">
              <h2 className="text-sm font-semibold text-slate-700">
                {g.property?.name} <span className="font-mono text-xs text-slate-500 ml-2">{g.property?.property_number}</span>
                <span className="ml-3 text-slate-600">· {g.year}</span>
              </h2>
              <div className="text-xs text-slate-600">
                Gesamt <b>{eur(g.sum)}</b> · Bezahlt <b className={g.paid >= g.sum ? "text-emerald-700" : ""}>{eur(g.paid)}</b>
              </div>
            </div>
            {Object.values(g.byUnit).map((row) => (
              <div key={row.unit?.id} className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-mono text-xs text-slate-500 font-semibold">{row.unit?.unit_number}</div>
                  <div className="text-xs text-slate-600">Jahr {eur(row.year)} · Bezahlt {eur(row.paid)}</div>
                </div>
                <div className="grid grid-cols-6 md:grid-cols-12 gap-1.5">
                  {MONTHS.map((m, i) => {
                    const month = i + 1;
                    const cell = row.months[month];
                    if (!cell) return <div key={m} className="text-center py-2 rounded bg-slate-50 text-slate-300 text-xs">—</div>;
                    const s = STATUS_META[cell.status] || STATUS_META.open;
                    const Icon = s.icon;
                    return (
                      <div
                        key={m}
                        className={`text-center py-1.5 rounded text-xs ${cell.status === "paid" ? "bg-emerald-50 text-emerald-800" : cell.status === "partial" ? "bg-blue-50 text-blue-800" : "bg-amber-50 text-amber-800"}`}
                        title={`${cell.matching_reference} · ${s.label}`}
                        data-testid={`owner-hausgeld-cell-${row.unit?.unit_number}-${month}`}
                      >
                        <div className="font-medium">{m}</div>
                        <Icon size={11} className="inline" weight="fill" />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </section>
        ))
      )}

      {Object.keys(grouped).length > 0 && (
        <div className="text-xs text-slate-500 border-t border-slate-200 pt-3">
          Zahlungshinweis: Bitte tragen Sie in Ihrem Überweisungs-Verwendungszweck die jeweilige Referenz <code className="bg-slate-100 px-1 text-[11px]">HG-{"{Einheit}"}-JAHR-MM</code> ein — dann wird Ihre Zahlung automatisch zugeordnet.
        </div>
      )}
    </div>
  );
}
